import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { LegacyCardComment, LessonCard } from "@shared/models/card-stack"
import type { Comment } from "@shared/models/comment"
import type { Permission } from "@shared/models/permissions"
import { handleError } from "cc-frontend/error-handling"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import { use } from "ember-could-get-used-to-this"

interface Sig {
  Args: {
    card: LessonCard
    cardStackId: string
    cardStackPermissions: Array<Permission>
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit
  }
  Element: HTMLDivElement
}

export default class Comments extends Component<Sig> {
  @service lessonEditor!: LessonEditor

  @use allComments = new FindManyAltogether(() => [
    "comment",
    get(this.args.card.attributes, "commentIds"),
  ])

  get hasComments(): boolean {
    return this.legacyComments.length > 0 || this.viewableComments.length > 0
  }

  get viewableComments(): Comment[] {
    if (get(this.allComments, "content") === null) return []

    return get(this.allComments, "content")!.filter(
      (comment) =>
        comment &&
        !get(comment.attributes, "deletedAt") &&
        comment.meta.permissions.includes("can-view-comment")
    )
  }

  // LEGACY EMBEDDED COMMENTS

  get legacyComments(): LegacyCardComment[] {
    return this.args.card.attributes.comments ?? []
  }

  @action
  removeLegacyComment(comment: LegacyCardComment) {
    window
      .swal({
        title: "Delete Comment?",
        text: "Are you sure you want to delete this comment?",
        type: "warning",
        showCancelButton: true,
      })
      .then(() => {
        this.lessonEditor.removeLegacyComment(
          this.args.modelsNeededForFirstEdit,
          this.args.cardStackId,
          this.args.card.id,
          comment
        )
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::CardStacks::Cards::Parts::Comments": typeof Comments
  }
}
